import { GenericPanelWithDropdown, InfoRow, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { MeetingOutputDTO, RoomOutputDTO, TimeEventOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { isArray } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import styles from "./TimeEventsAgenda.module.css";
import clsx from "clsx";

export interface TimeEventsAgendaProps {
  timeEvents?: TimeEventOutputDTO | TimeEventOutputDTO[];
  placeholdersNb: number;
  selectedEventId?: string;
  kind?: "student" | "expert" | "pedagogy";
  mode?: "class" | "replay";
  onSelectEvent?: (eventId: string) => void;
  onJoinEvent?: (eventId: string) => void;
}

interface EventData {
  id: string;
  title: string;
  roomName?: string;
  startDatetime: Date;
  endDatetime: Date;
  eventType: string;
  expertName: string;
  promotion?: string;
}

type EventStatus = "incoming" | "ongoing" | "finished";

const getEventStatus = (event: EventData): EventStatus => {
  const now = new Date();
  if (event.startDatetime > now) return "incoming";
  if (event.endDatetime < now) return "finished";
  return "ongoing";
}

export const convertTimeEvent = (
  event: TimeEventOutputDTO,
): EventData | undefined => {
  const meeting = event.target as MeetingOutputDTO;
  if (!meeting) return undefined;
  const expert = meeting.actors?.find((a) => a.externalProvider === "escaet:expert");
  const room = event.room as RoomOutputDTO; // populated room
  return {
    id: event._id,
    title: meeting.name || "",
    roomName: room?.name,
    startDatetime: new Date(event.startDatetime),
    endDatetime: new Date(event.endDatetime),
    eventType: "cours",
    expertName: expert?.identity?.username ?? expert?.user?.identity?.username ?? "",
    promotion: (meeting as any).promos?.join(' '),
  };
}

const SortMethods: Record<string, string> = {
  date: "Date",
  name: "Name",
  room: "Room",
};

const SortValues = Object.keys(SortMethods);

const EventItem = ({ data, mode = "class", onJoin }: { data: EventData, mode?: "class" | "replay", onJoin: () => void }) => {
  const [status, setStatus] = useState<EventStatus>(() => getEventStatus(data));

  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(getEventStatus(data));
    }, 5000);
    return () => clearInterval(interval);
  }, [data]);

  const renderStatus = () => (
    <p style={{ color: status === "ongoing" ? 'var(--color-element-calendar-item-status-ongoing)' : 'var(--color-element-calendar-item-status)' }}>
      Évènement {status === "incoming" ? "à venir" : status === "ongoing" ? "en cours" : "terminé"}
    </p>
  );

  return (
    <InfoRow
      date={data.startDatetime}
      header={data.roomName}
      title={data.title}
      leftIcon={<ReactIcons.Clock2 width={12} color={"var(--color-agenda-title)"} />}
      rightContent={renderStatus()}
      customButton={
        status === "ongoing" && ( 
          <PrimaryButton
            onClick={onJoin}
            className={clsx({
              [styles.joinBtn]: true,
              [styles.replay]: mode === "replay",
            })}
          >
            {mode === "replay" ? (
              <>
                <ReactIcons.PlayScreens className={styles.joinBtnIcon} />
                Visionner 
              </>
            ) : "Rejoindre"}
          </PrimaryButton>
        )
      }
    />
  )
}; 


export const TimeEventsAgenda = ({ timeEvents, placeholdersNb, selectedEventId, kind, mode, onSelectEvent, onJoinEvent }: TimeEventsAgendaProps) => {
  const [sortMethod, setSortMethod] = useState<string>(SortValues[0]);

  const getDropdownLabel = useCallback((value: string) => {
    // Todo : translate
    return SortMethods[value] || "Unknown";
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSortMethod(value);
  }, []);

  const eventsData = useMemo<EventData[] | undefined>(() => {
    if (!timeEvents) return undefined;
    const _timeEvents = isArray(timeEvents) ? timeEvents : [timeEvents];
    const eventsData: EventData[] = [];
    _timeEvents.forEach((timeEvent) => {
      const eventData = convertTimeEvent(timeEvent);
      if (eventData) eventsData.push(eventData);
    });
    return eventsData;
  }, [timeEvents]);

  const sortedEventsData = useMemo<EventData[] | undefined>(() => {
    if (!eventsData) return undefined;
    return [...eventsData].sort((a, b) => {
      switch (sortMethod) {
        case "date":
          return a.startDatetime.getTime() - b.startDatetime.getTime();
        case "name":
          return a.title.localeCompare(b.title);
        case "room":
          return (a.roomName || "").localeCompare(b.roomName || "");
        default:
          return 0;
      }
    });
  }, [eventsData, sortMethod]);

  const eventNodes = useMemo(() => {
    if (!sortedEventsData) return Array.from({ length: placeholdersNb }).map((_, index) => (<InfoRow.Placeholder key={index} />));
    if (sortedEventsData.length === 0) {
      return [<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>Aucun évènement trouvé</div>]
    }
    return sortedEventsData.map((event) => (
      <EventItem 
        key={event.id} 
        data={event} 
        mode={mode}
        onJoin={() => onJoinEvent?.(event.id)}
      />
    ));
  }, [sortedEventsData]);
  
  if (eventsData && !isArray(timeEvents)) {
    const data = eventsData[0];
    if (!data) {
      return (
        <div style={{ width: '100%', borderRadius: '15px', height: '200px', backgroundColor: 'rgb(102 102 102)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'lightgrey'}}>
          L'event ne semble pas exister
        </div>
      );
    }
  }
    
  return (
    <GenericPanelWithDropdown
      style={{ 
        height: "100%",
        boxShadow: "var(--shadow-element-box)",
      }}
      items={eventNodes}
      mainTitle="AGENDA"
      subtitle={mode === "replay" ? "Replays" : "Activités"}
      dropdownOptions={eventNodes.length > 1 ? SortValues : undefined}
      getDropdownLabel={getDropdownLabel}
      onSelectOption={handleSortChange}
    />
  );
};
